import React from 'react';
import { Box, Typography, Chip, IconButton, Dialog, DialogTitle, DialogContent, Drawer } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import LedgerBillDetails from './LedgerBillDetails';
import CopyButton from '../common/CopyButton';
import { formatDateTimeAsSent } from '../../../utils/dateUtils';

/**
 * Transaction detail view: a bottom drawer on mobile, a dialog on desktop.
 * Both render the same body, defined once here so they cannot drift apart.
 */
const LedgerTransactionDialog = ({
  transaction,
  isMobile,
  drawerOpen,
  onCloseDrawer,
  onCloseDialog,
  billDetails,
  loadingBillDetails,
}) => {
  if (!transaction) return null;

  const body = (
    <>
      <Box sx={{ p: 1.5, bgcolor: 'background.default', borderRadius: 1 }}>
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
          Narration
        </Typography>
        <Typography variant="body2">{transaction.narration}</Typography>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.5, bgcolor: 'background.default', borderRadius: 1 }}>
        <Typography variant="subtitle2" color="text.secondary" sx={{ minWidth: 120 }}>
          Type
        </Typography>
        <Chip label={transaction.ledger_type} size="small" color="primary" variant="outlined" />
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.5, bgcolor: 'background.default', borderRadius: 1 }}>
        <Typography variant="subtitle2" color="text.secondary" sx={{ minWidth: 120 }}>
          Bill ID
        </Typography>
        <Typography variant="body2" sx={{ minWidth: 0, overflowWrap: 'anywhere' }}>{transaction.bill_id}</Typography>
        {transaction.bill_id && <CopyButton value={transaction.bill_id} label="bill ID" />}
      </Box>

      <LedgerBillDetails
        transaction={transaction}
        billDetails={billDetails}
        loading={loadingBillDetails}
      />
    </>
  );

  return (
    <>
      {/* Mobile Drawer */}
      {isMobile && (
        <Drawer
          anchor="bottom"
          open={drawerOpen}
          onClose={onCloseDrawer}
          PaperProps={{
            sx: {
              height: '85vh',
              borderTopLeftRadius: 20,
              borderTopRightRadius: 20,
              p: 0,
              overflow: 'hidden'
            }
          }}
        >
          <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            {/* Header */}
            <Box sx={{ 
              p: 2, 
              borderBottom: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.paper',
              position: 'sticky',
              top: 0,
              zIndex: 1
            }}>
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                mb: 1
              }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Transaction Details
                </Typography>
                <IconButton 
                  onClick={onCloseDrawer}
                  sx={{ 
                    color: 'text.secondary',
                    '&:hover': { color: 'primary.main' }
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </Box>
              <Typography variant="subtitle2" color="text.secondary">
                {formatDateTimeAsSent(transaction.created_at)}
              </Typography>
            </Box>

            {/* Content */}
            <Box sx={{ 
              p: 2, 
              flex: 1, 
              overflow: 'auto',
              display: 'flex',
              flexDirection: 'column',
              gap: 2
            }}>
              {body}
            </Box>
          </Box>
        </Drawer>
      )}

      {/* Desktop Modal */}
      {!isMobile && (
        <Dialog
          open={Boolean(transaction)}
          onClose={onCloseDialog}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 2,
              overflow: 'hidden'
            }
          }}
        >
          {/* Header */}
          <DialogTitle sx={{ 
            p: 2, 
            borderBottom: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper'
          }}>
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              mb: 1
            }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Transaction Details
              </Typography>
              <IconButton 
                onClick={onCloseDialog}
                sx={{ 
                  color: 'text.secondary',
                  '&:hover': { color: 'primary.main' }
                }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
            <Typography variant="subtitle2" color="text.secondary">
              {formatDateTimeAsSent(transaction.created_at)}
            </Typography>
          </DialogTitle>

          <DialogContent sx={{ p: 0 }}>
            <Box sx={{ 
              p: 2, 
              display: 'flex',
              flexDirection: 'column',
              gap: 2
            }}>
              {body}
            </Box>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};

export default LedgerTransactionDialog;
